import { create } from "zustand";
import { truncateBranchAfter } from "./conversations";
import {
  createConversation,
  DEFAULT_ASSISTANTS,
  DEFAULT_SETTINGS,
  normalizeAssistant,
  normalizeQuestionMode,
} from "./defaults";
import {
  deleteArtifactData,
  deleteAssistantData,
  deleteConversationData,
  deleteDatabase,
  deleteProjectData,
  deleteQuestionSession,
  importArchive,
  loadBootstrap,
  loadConversationPage,
  loadConversationMessages,
  replaceConversationMessages,
  saveArtifact,
  saveAssistant,
  saveConversation,
  saveMessage,
  saveProject,
  saveQuestionSession,
  saveSettings,
} from "./indexedDb";
import type {
  AppSettings,
  Artifact,
  AssistantPersona,
  ChatMessage,
  Conversation,
  ModelInfo,
  PortableArchive,
  Project,
  Provider,
  ProviderStatus,
  QuestionSession,
} from "./types";

export type StorageStatus =
  | "idle"
  | "opening"
  | "ready"
  | "writing"
  | "error"
  | "unavailable";

export interface AppState {
  conversations: Conversation[];
  activeConversationId: string;
  projects: Project[];
  assistants: AssistantPersona[];
  /** Artifacts for the conversations whose messages have been paged in. */
  artifacts: Artifact[];
  activeArtifactId: string;
  settings: AppSettings;
  questionSessions: QuestionSession[];
  modelCatalog: Record<Provider, ModelInfo[]>;
  providerStatuses: Partial<Record<Exclude<Provider, "universal">, ProviderStatus>>;
  modelsLoading: boolean;
  modelsError: string | undefined;
  searchQuery: string;
  mobileSidebar: boolean;
  hydrated: boolean;
  storageStatus: StorageStatus;
  storageError: string | undefined;
  hasMoreConversations: boolean;
  hydrate: () => Promise<void>;
  loadMessages: (conversationId: string) => Promise<void>;
  loadMoreConversations: () => Promise<void>;
  setModelCatalog: (
    models: Record<Provider, ModelInfo[]>,
    statuses: Partial<Record<Exclude<Provider, "universal">, ProviderStatus>>,
    error?: string,
  ) => void;
  setModelsLoading: (loading: boolean) => void;
  setSearchQuery: (value: string) => void;
  setMobileSidebar: (value: boolean) => void;
  setSettings: (patch: Partial<AppSettings>) => void;
  setParams: (patch: Partial<AppSettings["params"]>) => void;
  setCouncil: (patch: Partial<AppSettings["council"]>) => void;
  setAgentTeam: (patch: Partial<AppSettings["agentTeam"]>) => void;
  createConversation: () => string;
  selectConversation: (id: string) => void;
  updateConversation: (id: string, patch: Partial<Conversation>) => void;
  importConversation: (conversation: Conversation, artifacts?: Artifact[]) => void;
  deleteConversation: (id: string) => void;
  addMessage: (conversationId: string, message: ChatMessage) => void;
  updateMessage: (
    conversationId: string,
    messageId: string,
    patch: Partial<ChatMessage>,
  ) => void;
  truncateAfter: (conversationId: string, messageId: string) => void;
  replaceAll: (data: PortableArchive) => Promise<void>;
  clearAllData: () => Promise<void>;
  addProject: (project: Project) => void;
  updateProject: (id: string, patch: Partial<Project>) => void;
  deleteProject: (id: string) => void;
  addAssistant: (assistant: AssistantPersona) => void;
  updateAssistant: (id: string, patch: Partial<AssistantPersona>) => void;
  deleteAssistant: (id: string) => void;
  upsertQuestionSession: (session: QuestionSession) => void;
  removeQuestionSession: (id: string) => void;
  addArtifacts: (artifacts: Artifact[]) => void;
  updateArtifact: (id: string, patch: Partial<Artifact>) => void;
  restoreArtifactRevision: (id: string, index: number) => void;
  deleteArtifact: (id: string) => void;
  openArtifact: (id: string) => void;
}

const emptyCatalog: Record<Provider, ModelInfo[]> = {
  openrouter: [],
  gemini: [],
  universal: [],
};

function persistenceError(
  set: (patch: Partial<AppState>) => void,
  error: unknown,
): void {
  const message = error instanceof Error ? error.message : String(error);
  set({ storageStatus: "error", storageError: message });
}

function runWrite(
  set: (patch: Partial<AppState>) => void,
  operation: Promise<void>,
): void {
  set({ storageStatus: "writing", storageError: undefined });
  void operation.then(
    () => set({ storageStatus: "ready", storageError: undefined }),
    (error) => persistenceError(set, error),
  );
}

/**
 * Edits closer together than this share one revision. Without coalescing, every
 * keystroke in the artifact editor would push a snapshot and the history would
 * be 30 single-character steps instead of meaningful checkpoints.
 */
const REVISION_COALESCE_MS = 15_000;
const MAX_REVISIONS = 30;

let parameterWriteTimer: ReturnType<typeof setTimeout> | undefined;

function debounceParameterWrite(
  set: (patch: Partial<AppState>) => void,
  settings: AppSettings,
): void {
  if (parameterWriteTimer) clearTimeout(parameterWriteTimer);
  set({ storageStatus: "writing", storageError: undefined });
  parameterWriteTimer = setTimeout(() => {
    parameterWriteTimer = undefined;
    void saveSettings(settings).then(
      () => set({ storageStatus: "ready", storageError: undefined }),
      (error) => persistenceError(set, error),
    );
  }, 250);
}

export const useAppStore = create<AppState>((set, get) => ({
  conversations: [],
  activeConversationId: "",
  projects: [],
  assistants: [],
  artifacts: [],
  activeArtifactId: "",
  settings: DEFAULT_SETTINGS,
  questionSessions: [],
  modelCatalog: emptyCatalog,
  providerStatuses: {},
  modelsLoading: true,
  modelsError: undefined,
  searchQuery: "",
  mobileSidebar: false,
  hydrated: false,
  storageStatus: "idle",
  storageError: undefined,
  hasMoreConversations: true,

  hydrate: async () => {
    if (get().hydrated || get().storageStatus === "opening") return;
    set({ storageStatus: "opening", storageError: undefined });
    try {
      const data = await loadBootstrap();
      let conversations = data.conversations;
      if (!conversations.length) {
        const initial = createConversation();
        conversations = [initial];
        await saveConversation(initial);
      }
      set({
        conversations,
        activeConversationId: conversations[0]!.id,
        projects: data.projects,
        assistants: data.assistants,
        settings: normalizeSettings(data.settings),
        questionSessions: data.questionSessions,
        hydrated: true,
        storageStatus: "ready",
        hasMoreConversations: data.conversations.length >= 30,
      });
      await get().loadMessages(conversations[0]!.id);
    } catch (error) {
      const initial = createConversation();
      set({
        conversations: [initial],
        activeConversationId: initial.id,
        assistants: DEFAULT_ASSISTANTS,
        hydrated: true,
        storageStatus: globalThis.indexedDB ? "error" : "unavailable",
        storageError: error instanceof Error ? error.message : String(error),
      });
    }
  },

  loadMoreConversations: async () => {
    const current = get().conversations;
    if (!current.length || !get().hasMoreConversations) return;
    const oldest = current.reduce((candidate, conversation) => {
      if (conversation.updatedAt < candidate.updatedAt) return conversation;
      if (
        conversation.updatedAt === candidate.updatedAt &&
        conversation.id < candidate.id
      ) {
        return conversation;
      }
      return candidate;
    });
    try {
      const page = await loadConversationPage(
        oldest.updatedAt,
        oldest.id,
      );
      set((state) => ({
        conversations: [
          ...state.conversations,
          ...page.filter(
            (item) =>
              !state.conversations.some(
                (conversation) => conversation.id === item.id,
              ),
          ),
        ],
        hasMoreConversations: page.length >= 30,
      }));
    } catch (error) {
      persistenceError(set, error);
    }
  },

  loadMessages: async (conversationId) => {
    const current = get().conversations.find((item) => item.id === conversationId);
    if (!current || current.messagesLoaded) return;
    try {
      const loaded = await loadConversationMessages(conversationId);
      set((state) => ({
        conversations: state.conversations.map((conversation) =>
          conversation.id === conversationId
            ? { ...conversation, messages: loaded.messages, messagesLoaded: true }
            : conversation,
        ),
        artifacts: [
          ...state.artifacts.filter(
            (artifact) => artifact.conversationId !== conversationId,
          ),
          ...loaded.artifacts,
        ],
      }));
    } catch (error) {
      persistenceError(set, error);
    }
  },

  setModelCatalog: (modelCatalog, providerStatuses, modelsError) =>
    set({
      modelCatalog,
      providerStatuses,
      modelsError,
      modelsLoading: false,
    }),
  setModelsLoading: (modelsLoading) => set({ modelsLoading }),
  setSearchQuery: (searchQuery) => set({ searchQuery }),
  setMobileSidebar: (mobileSidebar) => set({ mobileSidebar }),

  setSettings: (patch) => {
    const settings = normalizeSettings({ ...get().settings, ...patch });
    set({ settings });
    runWrite(set, saveSettings(settings));
  },
  setParams: (patch) => {
    const settings = normalizeSettings({
      ...get().settings,
      params: { ...get().settings.params, ...patch },
    });
    set({ settings });
    debounceParameterWrite(set, settings);
  },
  setCouncil: (patch) => {
    const settings = normalizeSettings({
      ...get().settings,
      council: { ...get().settings.council, ...patch },
    });
    set({ settings });
    runWrite(set, saveSettings(settings));
  },
  setAgentTeam: (patch) => {
    const settings = normalizeSettings({
      ...get().settings,
      agentTeam: { ...get().settings.agentTeam, ...patch },
    });
    set({ settings });
    runWrite(set, saveSettings(settings));
  },

  createConversation: () => {
    const conversation = createConversation();
    set((state) => ({
      conversations: [conversation, ...state.conversations],
      activeConversationId: conversation.id,
      mobileSidebar: false,
    }));
    runWrite(set, saveConversation(conversation));
    return conversation.id;
  },
  selectConversation: (activeConversationId) => {
    if (!get().conversations.some((conversation) => conversation.id === activeConversationId)) {
      return;
    }
    set({ activeConversationId, mobileSidebar: false });
    void get().loadMessages(activeConversationId);
  },
  updateConversation: (id, patch) => {
    let updated: Conversation | undefined;
    set((state) => ({
      conversations: state.conversations.map((item) => {
        if (item.id !== id) return item;
        updated = { ...item, ...patch, updatedAt: Date.now() };
        return updated;
      }),
    }));
    if (updated) {
      runWrite(
        set,
        patch.messages
          ? replaceConversationMessages(updated)
          : saveConversation(updated),
      );
    }
  },
  importConversation: (conversation, artifacts = []) => {
    const imported = { ...conversation, messagesLoaded: true };
    set((state) => ({
      conversations: [imported, ...state.conversations],
      activeConversationId: imported.id,
      artifacts: [...state.artifacts, ...artifacts],
      mobileSidebar: false,
    }));
    runWrite(
      set,
      Promise.all([
        replaceConversationMessages(imported),
        ...artifacts.map(saveArtifact),
      ]).then(() => undefined),
    );
  },
  deleteConversation: (id) => {
    const wasActive = get().activeConversationId === id;
    let remaining = get().conversations.filter((item) => item.id !== id);
    const writes = [deleteConversationData(id)];
    if (!remaining.length) {
      const replacement = createConversation();
      remaining = [replacement];
      writes.push(saveConversation(replacement));
    }
    const nextActiveId = wasActive ? remaining[0]!.id : get().activeConversationId;
    set({
      conversations: remaining,
      activeConversationId: nextActiveId,
      questionSessions: get().questionSessions.filter(
        (session) => session.conversationId !== id,
      ),
      artifacts: get().artifacts.filter(
        (artifact) => artifact.conversationId !== id,
      ),
    });
    if (wasActive) void get().loadMessages(nextActiveId);
    runWrite(set, Promise.all(writes).then(() => undefined));
  },
  addMessage: (conversationId, message) => {
    let order = 0;
    let conversation: Conversation | undefined;
    set((state) => ({
      conversations: state.conversations.map((item) => {
        if (item.id !== conversationId) return item;
        order = item.messages.length;
        conversation = {
          ...item,
          messages: [...item.messages, message],
          messagesLoaded: true,
          updatedAt: Date.now(),
        };
        return conversation;
      }),
    }));
    if (conversation) {
      runWrite(
        set,
        Promise.all([
          saveMessage(conversationId, message, order),
          saveConversation(conversation),
        ]).then(() => undefined),
      );
    }
  },
  updateMessage: (conversationId, messageId, patch) => {
    let updated: ChatMessage | undefined;
    let updatedConversation: Conversation | undefined;
    let order = -1;
    set((state) => ({
      conversations: state.conversations.map((conversation) => {
        if (conversation.id !== conversationId) return conversation;
        const messages = conversation.messages.map((message, index) => {
          if (message.id !== messageId) return message;
          order = index;
          updated = { ...message, ...patch };
          return updated;
        });
        updatedConversation = { ...conversation, messages, updatedAt: Date.now() };
        return updatedConversation;
      }),
    }));
    const terminal =
      patch.status !== undefined && patch.status !== "streaming";
    if (updated && updatedConversation && order >= 0 && terminal) {
      runWrite(
        set,
        Promise.all([
          saveMessage(conversationId, updated, order),
          saveConversation(updatedConversation),
        ]).then(() => undefined),
      );
    }
  },
  truncateAfter: (conversationId, messageId) => {
    let updated: Conversation | undefined;
    set((state) => ({
      conversations: state.conversations.map((conversation) => {
        if (conversation.id !== conversationId) return conversation;
        updated = {
          ...conversation,
          messages: truncateBranchAfter(conversation.messages, messageId),
          updatedAt: Date.now(),
        };
        return updated;
      }),
    }));
    if (updated) runWrite(set, replaceConversationMessages(updated));
  },

  replaceAll: async (data) => {
    set({ storageStatus: "writing", storageError: undefined });
    try {
      const settings = normalizeSettings(data.settings);
      await importArchive({ ...data, settings });
      const conversations = data.conversations.map((conversation) => ({
        ...conversation,
        messagesLoaded: true,
        mode: conversation.mode ?? "standard",
        questionMode: normalizeQuestionMode(conversation.questionMode),
      }));
      const replacement = conversations.length
        ? conversations
        : [createConversation()];
      if (!conversations.length) await saveConversation(replacement[0]!);
      set({
        conversations: replacement,
        activeConversationId: replacement[0]!.id,
        projects: data.projects,
        assistants: data.assistants.length
          ? data.assistants.map(normalizeAssistant)
          : DEFAULT_ASSISTANTS,
        artifacts: data.artifacts ?? [],
        activeArtifactId: "",
        settings,
        questionSessions: data.questionSessions ?? [],
        storageStatus: "ready",
        storageError: undefined,
        hasMoreConversations: false,
      });
    } catch (error) {
      persistenceError(set, error);
      throw error;
    }
  },

  clearAllData: async () => {
    set({ storageStatus: "writing", storageError: undefined });
    await deleteDatabase();
    const conversation = createConversation();
    set({
      conversations: [conversation],
      activeConversationId: conversation.id,
      projects: [],
      assistants: DEFAULT_ASSISTANTS,
      artifacts: [],
      activeArtifactId: "",
      settings: DEFAULT_SETTINGS,
      questionSessions: [],
      storageStatus: "ready",
      storageError: undefined,
      hasMoreConversations: false,
    });
    await Promise.all([
      saveConversation(conversation),
      saveSettings(DEFAULT_SETTINGS),
    ]);
  },

  addProject: (project) => {
    set((state) => ({ projects: [...state.projects, project] }));
    runWrite(set, saveProject(project));
  },
  updateProject: (id, patch) => {
    let updated: Project | undefined;
    set((state) => ({
      projects: state.projects.map((project) => {
        if (project.id !== id) return project;
        updated = { ...project, ...patch };
        return updated;
      }),
    }));
    if (updated) runWrite(set, saveProject(updated));
  },
  deleteProject: (id) => {
    set((state) => ({
      projects: state.projects.filter((project) => project.id !== id),
      conversations: state.conversations.map((conversation) => {
        if (conversation.projectId !== id) return conversation;
        const { projectId: _projectId, ...next } = conversation;
        return next;
      }),
    }));
    runWrite(set, deleteProjectData(id));
  },
  addAssistant: (assistant) => {
    set((state) => ({ assistants: [...state.assistants, assistant] }));
    runWrite(set, saveAssistant(assistant));
  },
  updateAssistant: (id, patch) => {
    let updated: AssistantPersona | undefined;
    set((state) => ({
      assistants: state.assistants.map((assistant) => {
        if (assistant.id !== id) return assistant;
        updated = { ...assistant, ...patch };
        return updated;
      }),
    }));
    if (updated) runWrite(set, saveAssistant(updated));
  },
  deleteAssistant: (id) => {
    set((state) => ({
      assistants: state.assistants.filter((assistant) => assistant.id !== id),
    }));
    runWrite(set, deleteAssistantData(id));
  },

  upsertQuestionSession: (session) => {
    set((state) => ({
      questionSessions: [
        session,
        ...state.questionSessions.filter((item) => item.id !== session.id),
      ],
    }));
    runWrite(set, saveQuestionSession(session));
  },
  removeQuestionSession: (id) => {
    set((state) => ({
      questionSessions: state.questionSessions.filter(
        (session) => session.id !== id,
      ),
    }));
    runWrite(set, deleteQuestionSession(id));
  },

  addArtifacts: (incoming) => {
    if (!incoming.length) return;
    set((state) => ({
      artifacts: [
        ...state.artifacts.filter(
          (artifact) => !incoming.some((item) => item.id === artifact.id),
        ),
        ...incoming,
      ],
      activeArtifactId: incoming[incoming.length - 1]!.id,
    }));
    runWrite(
      set,
      Promise.all(incoming.map(saveArtifact)).then(() => undefined),
    );
  },
  updateArtifact: (id, patch) => {
    let updated: Artifact | undefined;
    const now = Date.now();
    set((state) => ({
      artifacts: state.artifacts.map((artifact) => {
        if (artifact.id !== id) return artifact;
        // A content change snapshots the previous body so that an overwrite —
        // by a later model turn or by hand — stays recoverable. Rapid edits
        // fold into the checkpoint already taken at the start of the burst.
        const contentChanged =
          patch.content !== undefined && patch.content !== artifact.content;
        const checkpoint =
          contentChanged && now - artifact.updatedAt >= REVISION_COALESCE_MS;
        updated = {
          ...artifact,
          ...patch,
          revisions: checkpoint
            ? [
                ...artifact.revisions,
                {
                  content: artifact.content,
                  createdAt: artifact.updatedAt,
                  note: artifact.title,
                },
              ].slice(-MAX_REVISIONS)
            : artifact.revisions,
          updatedAt: now,
        };
        return updated;
      }),
    }));
    if (updated) runWrite(set, saveArtifact(updated));
  },
  restoreArtifactRevision: (id, index) => {
    const artifact = get().artifacts.find((item) => item.id === id);
    const revision = artifact?.revisions[index];
    if (!artifact || !revision) return;
    get().updateArtifact(id, { content: revision.content });
  },
  deleteArtifact: (id) => {
    set((state) => ({
      artifacts: state.artifacts.filter((artifact) => artifact.id !== id),
      activeArtifactId:
        state.activeArtifactId === id ? "" : state.activeArtifactId,
    }));
    runWrite(set, deleteArtifactData(id));
  },
  openArtifact: (activeArtifactId) => {
    set((state) => ({
      activeArtifactId,
      settings: { ...state.settings, rightPanel: "artifact" },
    }));
    runWrite(set, saveSettings(get().settings));
  },
}));

export function normalizeSettings(settings?: Partial<AppSettings>): AppSettings {
  const provider = (value: unknown, fallback: Provider): Provider =>
    value === "openrouter" || value === "gemini" || value === "universal"
      ? value
      : fallback;
  const text = (value: unknown, fallback: string): string =>
    typeof value === "string" ? value : fallback;
  const boolean = (value: unknown, fallback: boolean): boolean =>
    typeof value === "boolean" ? value : fallback;
  const number = (
    value: unknown,
    fallback: number,
    min: number,
    max: number,
    integer = false,
  ): number => {
    const candidate = typeof value === "number" && Number.isFinite(value)
      ? value
      : fallback;
    const bounded = Math.min(max, Math.max(min, candidate));
    return integer ? Math.round(bounded) : bounded;
  };
  const modelRef = (
    value: Partial<{ provider: Provider; model: string }> | undefined,
    fallback: { provider: Provider; model: string },
  ) => ({
    provider: provider(value?.provider, fallback.provider),
    model: text(value?.model, fallback.model),
  });
  const rawAgents = settings?.agentTeam?.agents;
  const agents = Array.isArray(rawAgents)
    ? rawAgents.map((agent, index) => {
        const fallback =
          DEFAULT_SETTINGS.agentTeam.agents[index] ??
          DEFAULT_SETTINGS.agentTeam.agents[0]!;
        return {
          id: text(agent?.id, `agent-${index + 1}`),
          name: text(agent?.name, fallback.name),
          role: text(agent?.role, fallback.role),
          instructions: text(agent?.instructions, fallback.instructions),
          ...modelRef(agent, fallback),
          dependsOn: Array.isArray(agent?.dependsOn)
            ? agent.dependsOn.filter((id): id is string => typeof id === "string" && !!id)
            : [...fallback.dependsOn],
        };
      })
    : DEFAULT_SETTINGS.agentTeam.agents;
  const rawCouncilModels = settings?.council?.models;
  const councilModels = Array.isArray(rawCouncilModels)
    ? rawCouncilModels.map((item, index) =>
        modelRef(item, DEFAULT_SETTINGS.council.models[index] ?? DEFAULT_SETTINGS.council.models[0]!),
      )
    : DEFAULT_SETTINGS.council.models;
  const params = settings?.params;
  const theme =
    settings?.theme === "light" || settings?.theme === "system" || settings?.theme === "dark"
      ? settings.theme
      : DEFAULT_SETTINGS.theme;
  const rightPanel =
    settings?.rightPanel === "artifact" || settings?.rightPanel === "raw" || settings?.rightPanel === "none"
      ? settings.rightPanel
      : DEFAULT_SETTINGS.rightPanel;
  const chatModel = modelRef(settings?.modelDefaults?.chat, DEFAULT_SETTINGS.modelDefaults.chat);
  const council = settings?.council;
  const agentTeam = settings?.agentTeam;
  return {
    ...DEFAULT_SETTINGS,
    ...settings,
    provider: provider(settings?.provider, DEFAULT_SETTINGS.provider),
    model: text(settings?.model, DEFAULT_SETTINGS.model),
    systemPrompt: text(settings?.systemPrompt, DEFAULT_SETTINGS.systemPrompt),
    params: {
      temperature: number(params?.temperature, DEFAULT_SETTINGS.params.temperature, 0, 2),
      maxTokens: number(params?.maxTokens, DEFAULT_SETTINGS.params.maxTokens, 1, 65_536, true),
      topP: number(params?.topP, DEFAULT_SETTINGS.params.topP, 0, 1),
      topK: number(params?.topK, DEFAULT_SETTINGS.params.topK, 0, 1000, true),
      frequencyPenalty: number(params?.frequencyPenalty, DEFAULT_SETTINGS.params.frequencyPenalty, -2, 2),
      presencePenalty: number(params?.presencePenalty, DEFAULT_SETTINGS.params.presencePenalty, -2, 2),
      ...(typeof params?.seed === "number" && Number.isInteger(params.seed)
        ? { seed: params.seed }
        : {}),
    },
    webSearch: boolean(settings?.webSearch, DEFAULT_SETTINGS.webSearch),
    deepResearch: boolean(settings?.deepResearch, DEFAULT_SETTINGS.deepResearch),
    rawMode: boolean(settings?.rawMode, DEFAULT_SETTINGS.rawMode),
    rawPayloadOverride: text(settings?.rawPayloadOverride, DEFAULT_SETTINGS.rawPayloadOverride),
    theme,
    sidebarCollapsed: boolean(settings?.sidebarCollapsed, DEFAULT_SETTINGS.sidebarCollapsed),
    rightPanel,
    modelDefaults: {
      chat: chatModel,
    },
    council: {
      ...DEFAULT_SETTINGS.council,
      enabled: boolean(council?.enabled, DEFAULT_SETTINGS.council.enabled),
      models: councilModels,
      rounds: number(council?.rounds, DEFAULT_SETTINGS.council.rounds, 1, 5, true),
      visible: boolean(council?.visible, DEFAULT_SETTINGS.council.visible),
      maxCost: number(council?.maxCost, DEFAULT_SETTINGS.council.maxCost, 0, 1000),
      synthesis: {
        ...modelRef(council?.synthesis, DEFAULT_SETTINGS.council.synthesis),
      },
    },
    agentTeam: {
      ...DEFAULT_SETTINGS.agentTeam,
      enabled: boolean(agentTeam?.enabled, DEFAULT_SETTINGS.agentTeam.enabled),
      agents,
      visible: boolean(agentTeam?.visible, DEFAULT_SETTINGS.agentTeam.visible),
      maxCost: number(agentTeam?.maxCost, DEFAULT_SETTINGS.agentTeam.maxCost, 0, 1000),
      orchestrator: {
        ...modelRef(agentTeam?.orchestrator, DEFAULT_SETTINGS.agentTeam.orchestrator),
      },
      verification: {
        ...DEFAULT_SETTINGS.agentTeam.verification,
        enabled: boolean(
          agentTeam?.verification?.enabled,
          DEFAULT_SETTINGS.agentTeam.verification.enabled,
        ),
        instructions: text(
          agentTeam?.verification?.instructions,
          DEFAULT_SETTINGS.agentTeam.verification.instructions,
        ),
        ...modelRef(agentTeam?.verification, DEFAULT_SETTINGS.agentTeam.verification),
      },
      synthesizer: {
        ...modelRef(agentTeam?.synthesizer, DEFAULT_SETTINGS.agentTeam.synthesizer),
      },
    },
  };
}
